/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Filter,
  BarChart,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Users,
  User,
  ExternalLink,
} from "lucide-react";
import {
  fetchPolicies,
  fetchPolicyAreas,
  fetchMpsById,
  fetchMpVotesById,
} from "../../api/policy_tracker/policyAPI";
import Parliament3DView from "../../components/Parliament3DView"; // Import your existing Parliament3DView
import "../../styles/policy_tracker/PolicyTracker.css";

const PolicyTracker = () => {
  // State variables
  const [policies, setPolicies] = useState([]);
  const [policyAreas, setPolicyAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const [totalPolicies, setTotalPolicies] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Parliament view state

  // Temporary hover highlight state
  const [hoverParty, setHoverParty] = useState(null);
  const [hoverVote, setHoverVote] = useState(null);

  const [parliamentLoading, setParliamentLoading] = useState(false);
  const [mps, setMPs] = useState([]);
  const [partyData, setPartyData] = useState({});
  const [selectedMp, setSelectedMp] = useState(null);
  const [highlightParty, setHighlightParty] = useState(null);
  const [highlightVote, setHighlightVote] = useState(null);
  const [activeFilter, setActiveFilter] = useState("none"); // Initialize with "none"
  const [mpVotes, setMpVotes] = useState([]);

  useEffect(() => {
    const loadMpVotes = async () => {
      try {
        setLoading(true);
        // Delay search to avoid making API calls on every keystroke
        const delayDebounceFn = setTimeout(async () => {
          const result = await fetchMpVotesById(selectedMp.id);
          setMpVotes(result);
          console.log(mpVotes);
          setLoading(false);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
      } catch (err) {
        setError("Failed to load mp votes. Please try again later.");
        setLoading(false);
        console.error("Error loading mp votes:", err);
      }
    };
    if (selectedMp) {
      loadMpVotes();
    }
  }, [selectedMp]);
  // Debug helper to track state changes
  useEffect(() => {
    console.log("State changed:");
    console.log("  activeFilter:", activeFilter);
    console.log("  highlightParty:", highlightParty);
    console.log("  highlightVote:", highlightVote);
  }, [activeFilter, highlightParty, highlightVote]);

  // Handle party highlighting
  const handlePartyHighlight = (party) => {
    console.log("handlePartyHighlight called with party:", party);

    if (highlightParty === party) {
      console.log("Deactivating party:", party);
      setHighlightParty(null);
      setActiveFilter("none"); // Explicitly set to "none" string
    } else {
      console.log("Activating party:", party);
      setHighlightParty(party);
      setHighlightVote(null); // Clear vote highlight
      setActiveFilter(party);
    }
  };

  // Handle vote highlighting
  const handleVoteHighlight = (vote) => {
    console.log("handleVoteHighlight called with vote:", vote);

    if (highlightVote === vote) {
      console.log("Deactivating vote:", vote);
      setHighlightVote(null);
      setActiveFilter("none"); // Explicitly set to "none" string
    } else {
      console.log("Activating vote:", vote);
      setHighlightVote(vote);
      setHighlightParty(null); // Clear party highlight
      setActiveFilter(vote);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    console.log("Resetting all filters");
    setHighlightParty(null);
    setHighlightVote(null);
    setActiveFilter("none"); // Explicitly set to "none" string
  };

  // Fetch policies on initial load and when search/filter/pagination changes
  useEffect(() => {
    const loadPolicies = async () => {
      try {
        setLoading(true);
        // Delay search to avoid making API calls on every keystroke
        const delayDebounceFn = setTimeout(async () => {
          const result = await fetchPolicies(
            page,
            pageSize,
            searchTerm,
            filterArea
          );
          setPolicies(result.policies);
          setTotalPolicies(result.total);
          setTotalPages(Math.ceil(result.total / pageSize));
          setLoading(false);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
      } catch (err) {
        setError("Failed to load policies. Please try again later.");
        setLoading(false);
        console.error("Error loading policies:", err);
      }
    };

    loadPolicies();
  }, [page, pageSize, searchTerm, filterArea]);

  // Fetch policy areas once on initial load
  useEffect(() => {
    const loadPolicyAreas = async () => {
      try {
        const areas = await fetchPolicyAreas();
        setPolicyAreas(areas);
      } catch (err) {
        console.error("Error loading policy areas:", err);
        // Continue anyway, as this is not critical
      }
    };

    loadPolicyAreas();
  }, []);

  // Load MP data when a policy is expanded
  useEffect(() => {
    const loadParliamentData = async () => {
      if (expandedPolicy !== null) {
        try {
          setParliamentLoading(true);
          // Get the expanded policy
          const policy = policies[expandedPolicy];

          // Party mapping from full name to short code and color
          const partyMapping = {
            "Australian Labor Party": {
              code: "Labor",
              color: "#E81B23", // Labor red
            },
            "Liberal Party": {
              code: "Liberal",
              color: "#0047AB", // Liberal blue
            },
            "Liberal National Party": {
              code: "LNP",
              color: "#0047AB", // LNP dark blue
            },
            "Country Liberal Party": {
              code: "CWM",
              color: "#FF8C00", // Orange
            },
            Independent: {
              code: "Independent",
              color: "#808080", // Gray
            },
            "National Party": {
              code: "National",
              color: "#0047AB", // National dark green
            },
            "Australian Greens": {
              code: "Greens",
              color: "#00A651", // Greens bright green
            },
            "Centre Alliance": {
              code: "CA",
              color: "#FF6B00", // Orange
            },
          };

          // Fetch MPs for this division/bill
          const fetchedMPs = await fetchMpsById(policy.division_id);

          // Process MPs to ensure they have all required properties
          const processedMPs = fetchedMPs.map((mp) => {
            // Get party code from mapping or use existing
            const partyCode = partyMapping[mp.party]?.code || mp.party;

            // Process vote status to ensure it's either 'aye', 'no', or 'unknown'
            // This is critical to ensure the Parliament3DView works correctly
            let voteStatus = mp.voteStatus || mp.vote || "unknown";

            // Map any non-standard values to the expected ones
            if (voteStatus === "support" || voteStatus === "yes")
              voteStatus = "aye";
            if (voteStatus === "oppose" || voteStatus === "against")
              voteStatus = "no";

            // Add some debug logging to verify vote status
            console.log(
              `MP ${mp.name}: vote status ${voteStatus} (original: ${
                mp.voteStatus || mp.vote || "unknown"
              })`
            );

            return {
              ...mp,
              party: partyCode,
              voteStatus: voteStatus,
            };
          });

          // Log MP data for debugging
          console.log("Processed MPs:", processedMPs);

          // Generate party data for the 3D view
          const generatedPartyData = {};

          // Add all known parties to the party data
          Object.entries(partyMapping).forEach(([fullName, partyInfo]) => {
            generatedPartyData[partyInfo.code] = {
              name: fullName,
              color: partyInfo.color,
            };
          });
          console.log(generatedPartyData);
          // Extract unique parties from the MPs and ensure they exist in party data
          const uniqueParties = [
            ...new Set(processedMPs.map((mp) => mp.party)),
          ];

          // Add any missing parties
          uniqueParties.forEach((partyCode) => {
            if (!generatedPartyData[partyCode]) {
              generatedPartyData[partyCode] = {
                name: partyCode,
                color: "#808080", // Default to gray if color not found
              };
            }
          });

          // Set the processed data to state
          setMPs(processedMPs);
          setPartyData(generatedPartyData);
          setParliamentLoading(false);
        } catch (err) {
          console.error("Error loading parliament data:", err);
          setParliamentLoading(false);
        }
      }
    };

    loadParliamentData();
  }, [expandedPolicy, policies]);

  // Make sure to reset filters when toggling policy cards
  const toggleExpand = (index) => {
    setExpandedPolicy(expandedPolicy === index ? null : index);
    // Reset selected MP and filters when toggling policy cards
    setSelectedMp(null);
    resetFilters();
  };

  // Handle page changes
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
      window.scrollTo(0, 0);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on new search
  };

  // Handle policy area filter change
  const handleAreaChange = (area) => {
    setFilterArea(area === filterArea ? "" : area);
    setPage(1); // Reset to first page on new filter
  };

  // Calculate party support percentages for visualization
  const calculatePartySupport = (policy) => {
    const partyData = {};

    Object.entries(policy.parties).forEach(([party, details]) => {
      const totalVotes = details.votes_for + details.votes_against;
      const supportPercent =
        totalVotes > 0 ? (details.votes_for / totalVotes) * 100 : 0;

      partyData[party] = {
        ...details,
        supportPercent: Math.round(supportPercent),
      };
    });

    return partyData;
  };

  // Function to render party stance visualization
  const renderPartyStances = (policy) => {
    const partyData = calculatePartySupport(policy);

    return (
      <div className="party-stances">
        {Object.entries(partyData).map(([party, details]) => (
          <div
            key={party}
            className={`party-stance-row ${
              highlightParty === party ? "highlighted" : ""
            }`}
            // onClick={() => handlePartyHighlight(party)}
          >
            <div className="party-info">
              <div className="party-name">{party}</div>
              <div
                className={`stance-badge ${details.stance
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {details.stance}
              </div>
            </div>

            <div className="vote-bar-container">
              <div className="vote-bar">
                <div
                  className={`vote-bar-support ${
                    highlightVote === "aye" ? "highlighted" : ""
                  }`}
                  style={{ width: `${details.supportPercent}%` }}
                  // onClick={(e) => {
                  //   e.stopPropagation();
                  //   handleVoteHighlight('support');
                  // }}
                >
                  {details.votes_for > 0 && (
                    <span className="vote-count support">
                      {details.votes_for}
                    </span>
                  )}
                </div>
                <div
                  className={`vote-bar-oppose ${
                    highlightVote === "no" ? "highlighted" : ""
                  }`}
                  style={{ width: `${100 - details.supportPercent}%` }}
                  // onClick={(e) => {
                  //   e.stopPropagation();
                  //   handleVoteHighlight('no');
                  // }}
                >
                  {details.votes_against > 0 && (
                    <span className="vote-count oppose">
                      {details.votes_against}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="vote-legend">
          <div
            className={`legend-item ${highlightVote === "aye" ? "active" : ""}`}
            onClick={() => handleVoteHighlight("aye")}
          >
            <span className="legend-color support"></span>
            <span>Support</span>
          </div>
          <div
            className={`legend-item ${highlightVote === "no" ? "active" : ""}`}
            onClick={() => handleVoteHighlight("no")}
          >
            <span className="legend-color oppose"></span>
            <span>Oppose</span>
          </div>
        </div>
      </div>
    );
  };

  // Render MP details panel when an MP is selected
  const renderMPDetails = () => {
    if (!selectedMp) return null;

    return (
      <div className="mp-details-panel">
        <div className="mp-details-header">
          <User className="mp-icon" />
          <h3 className="mp-name">{selectedMp.name}</h3>
          <button className="close-button" onClick={() => setSelectedMp(null)}>
            ×
          </button>
        </div>

        <div className="mp-details-content">
          <div className="mp-stats">
            <div className="mp-stat">
              <span className="stat-label">Party:</span>
              <span className={`stat-value ${selectedMp.party.toLowerCase()}`}>
                {selectedMp.party}
              </span>
            </div>
            <div className="mp-stat">
              <span className="stat-label">Constituency:</span>
              <span className="stat-value">{selectedMp.electorate_name}</span>
            </div>
            <div className="mp-stat">
              <span className="stat-label">Vote:</span>
              <span className={`stat-value ${selectedMp.voteStatus}`}>
                {selectedMp.voteStatus === "aye"
                  ? "Support"
                  : selectedMp.voteStatus === "no"
                  ? "Oppose"
                  : "Unknown"}
              </span>
            </div>
          </div>

          {/* View Profile Link with React Router */}
          <Link
            to={`/mp/${encodeURIComponent(selectedMp.id)}`}
            className="view-profile-button router-link"
          >
            <ExternalLink size={16} />
            View Full Profile
          </Link>

          {/* Recent Voting History Section */}
          {mpVotes && mpVotes.length > 0 && (
            <div className="mp-recent-votes">
              <h4 className="votes-header">Recent Voting History</h4>
              <div className="votes-list">
                {mpVotes.map((voteItem, index) => (
                  <div key={index} className="vote-item">
                    <div className="vote-bill-name">{voteItem.bill_name}</div>
                    <div className={`vote-stance ${voteItem.vote_result}`}>
                      {voteItem.vote_result === "aye" ? "Support" : "Oppose"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    return (
      <div className="pagination">
        <button
          className="pagination-button prev"
          onClick={handlePrevPage}
          disabled={page <= 1}
        >
          <ChevronLeft size={16} />
          Previous
        </button>
        <div className="pagination-info">
          Page {page} of {totalPages} ({totalPolicies} policies)
        </div>
        <button
          className="pagination-button next"
          onClick={handleNextPage}
          disabled={page >= totalPages}
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  return (
    <div className="policy-tracker-container">
      <h1 className="policy-tracker-title">Policy Tracker</h1>
      <p className="policy-tracker-subtitle">
        Track Australian political party positions on key legislation
      </p>

      {/* Search and filter controls */}
      <div className="search-filter-container">
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search policies by name or content..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        <div className="filter-container">
          <Filter className="filter-icon" />
          <select
            className="filter-select"
            value={filterArea}
            onChange={(e) => handleAreaChange(e.target.value)}
          >
            <option value="">All Policy Areas</option>
            {policyAreas.map((area, index) => (
              <option key={index} value={area}>
                {area}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats summary */}
      <div className="policy-stats">
        <div className="stat-card">
          <BarChart className="stat-icon" />
          <div className="stat-content">
            <div className="stat-number">{totalPolicies}</div>
            <div className="pol-stat-label">Policies tracked</div>
          </div>
        </div>
        <div className="stat-areas">
          {policyAreas.map((area) => (
            <div
              key={area}
              className={`stat-area ${filterArea === area ? "active" : ""}`}
              onClick={() => handleAreaChange(area)}
            >
              {area}
            </div>
          ))}
        </div>
      </div>

      {/* Loading and error states */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading policies...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      )}

      {/* Pagination at top */}
      {!loading && !error && policies.length > 0 && renderPagination()}

      {/* Policy cards */}
      {!loading && !error && policies.length === 0 ? (
        <div className="no-results">
          <p>No policies match your search criteria</p>
        </div>
      ) : (
        <div className="policy-cards">
          {policies.map((policy, index) => (
            <div key={policy.bill_id} className="pt-policy-card">
              {/* Policy header - always visible */}
              <div
                className={`policy-header ${policy.POLICY_AREA.toLowerCase()}`}
                onClick={() => toggleExpand(index)}
              >
                <div className="policy-header-content">
                  <h3 className="policy-title">{policy.bill_name}</h3>
                  <div className="policy-tags">
                    <span
                      className={`policy-tag area-${policy.POLICY_AREA.toLowerCase().replace(
                        /\s+/g,
                        "-"
                      )}`}
                    >
                      {policy.POLICY_AREA}
                    </span>
                    <span className="policy-tag subarea">
                      {policy.POLICY_SUBAREA}
                    </span>
                    <span className="policy-tag date">
                      {new Date(policy.date).toLocaleDateString("en-AU")}
                    </span>
                  </div>
                </div>
                <div className="expand-icon">
                  {expandedPolicy === index ? (
                    <ChevronUp className="icon" />
                  ) : (
                    <ChevronDown className="icon" />
                  )}
                </div>
              </div>

              {/* Expanded policy details */}
              {expandedPolicy === index && (
                <div className="policy-details">
                  <div className="policy-overview">
                    <div className="policy-section main-purpose">
                      <h4 className="section-title">Main Purpose</h4>
                      <p className="section-content">{policy.MAIN_PURPOSE}</p>
                    </div>

                    <div className="policy-section key-provisions">
                      <h4 className="section-title">Key Provisions</h4>
                      <ul className="provisions-list">
                        {policy.KEY_PROVISIONS.map((provision, i) => (
                          <li key={i}>{provision}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="policy-section party-positions">
                    <h4 className="section-title">
                      <TrendingUp className="section-icon" />
                      Party Positions
                    </h4>

                    {renderPartyStances(policy)}
                  </div>

                  {/* Parliament View Section */}
                  <div className="policy-section parliament-view">
                    <h4 className="section-title">
                      <Users className="section-icon" />
                      Parliament View
                    </h4>

                    <div className="parliament-layout">
                      {parliamentLoading ? (
                        <div className="loading-state">
                          <div className="loading-spinner"></div>
                          <p>Loading parliament view...</p>
                        </div>
                      ) : (
                        <>
                          {/* Integration with Parliament3DView */}
                          <div className="parliament-container">
                            <div className="view-3d-container">
                              <Parliament3DView
                                mps={mps}
                                partyData={partyData}
                                selectedMp={selectedMp}
                                setSelectedMp={setSelectedMp}
                                highlightParty={hoverParty || highlightParty}
                                highlightVote={hoverVote || highlightVote}
                                activeFilter={
                                  // If there's a hover, use it as the active filter
                                  hoverParty
                                    ? hoverParty
                                    : hoverVote
                                    ? hoverVote
                                    : // Otherwise use the permanent filter, or "none" if there's no filter
                                    activeFilter === null || activeFilter === ""
                                    ? "none"
                                    : activeFilter
                                }
                              />
                            </div>

                            {/* Show MP details panel when an MP is selected */}
                            {selectedMp && renderMPDetails()}
                          </div>

                          {/* Legend section with proper hover behavior */}
                          <div className="legend-section">
                            <h3>Legend</h3>
                            <div className="legend-grid">
                              <div className="legend-category">
                                <h4>Parties</h4>
                                <div className="legend-items">
                                  {Object.keys(partyData).map((party) => (
                                    <div
                                      key={party}
                                      className={`legend-item ${
                                        highlightParty === party ? "active" : ""
                                      }`}
                                      onMouseEnter={() => setHoverParty(party)}
                                      onMouseLeave={() => setHoverParty(null)}
                                      onClick={() => {
                                        // Important: Check exactly with strict equality
                                        if (highlightParty === party) {
                                          // If this party is already selected, deselect it
                                          setHighlightParty(null);
                                          setActiveFilter("none");
                                        } else {
                                          // Otherwise select this party
                                          setHighlightParty(party);
                                          setHighlightVote(null); // Clear any vote highlight
                                          setActiveFilter(party);
                                        }
                                      }}
                                    >
                                      <span
                                        className="legend-color"
                                        style={{
                                          backgroundColor:
                                            partyData[party].color,
                                        }}
                                      ></span>
                                      <span className="legend-label">
                                        {party}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="legend-category">
                                <h4>Vote Status</h4>
                                <div className="legend-items">
                                  <div
                                    className={`legend-item ${
                                      highlightVote === "aye" ? "active" : ""
                                    }`}
                                    onMouseEnter={() => setHoverVote("aye")}
                                    onMouseLeave={() => setHoverVote(null)}
                                    onClick={() => {
                                      // Important: Use strict equality check
                                      if (highlightVote === "aye") {
                                        // If this vote is already selected, deselect it
                                        setHighlightVote(null);
                                        setActiveFilter("none");
                                      } else {
                                        // Otherwise select this vote
                                        setHighlightVote("aye");
                                        setHighlightParty(null); // Clear any party highlight
                                        setActiveFilter("aye");
                                      }
                                    }}
                                  >
                                    <span className="legend-color support"></span>
                                    <span className="legend-label">
                                      Support
                                    </span>
                                  </div>
                                  <div
                                    className={`legend-item ${
                                      highlightVote === "no" ? "active" : ""
                                    }`}
                                    onMouseEnter={() => setHoverVote("no")}
                                    onMouseLeave={() => setHoverVote(null)}
                                    onClick={() => {
                                      if (highlightVote === "no") {
                                        setHighlightVote(null);
                                        setActiveFilter("none");
                                      } else {
                                        setHighlightVote("no");
                                        setHighlightParty(null);
                                        setActiveFilter("no");
                                      }
                                    }}
                                  >
                                    <span className="legend-color oppose"></span>
                                    <span className="legend-label">Oppose</span>
                                  </div>
                                  <div
                                    className={`legend-item ${
                                      highlightVote === "unknown"
                                        ? "active"
                                        : ""
                                    }`}
                                    onMouseEnter={() => setHoverVote("unknown")}
                                    onMouseLeave={() => setHoverVote(null)}
                                    onClick={() => {
                                      if (highlightVote === "unknown") {
                                        setHighlightVote(null);
                                        setActiveFilter("none");
                                      } else {
                                        setHighlightVote("unknown");
                                        setHighlightParty(null);
                                        setActiveFilter("unknown");
                                      }
                                    }}
                                  >
                                    <span className="legend-color unknown"></span>
                                    <span className="legend-label">
                                      Unknown/Abstain
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="policy-footer">
                    <a
                      href={policy.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-bill-link"
                    >
                      View Official Bill Details →
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination at bottom */}
      {!loading && !error && policies.length > 0 && renderPagination()}
    </div>
  );
};

export default PolicyTracker;
